/* The Privacy Policy and Terms, in plain language. Keep LEGAL_VERSION in step
   with api/app/services/safety.py: changing it asks every member to accept again.
   Draft for the pilot — to be reviewed by counsel before a wider launch. */

export const LEGAL_VERSION = '2026-10-09';
export const OPERATOR = 'GenAImpact Inc.';
export const CONTACT = 'jaypersanchez@genaimpact.com';

export const PRIVACY = {
  title: 'Privacy Policy',
  updated: 'October 9, 2026',
  intro: `Builders' Network ("B/N") is a private, invite-only network operated by ${OPERATOR}, a company in the Philippines. This explains what we collect, why, and the choices you have. We wrote it to be read, not skimmed past.`,
  sections: [
    ['What we collect', [
      'Your account: name, email, and — if you use it — your Google account ID. If you use a password, we store only a scrambled version of it, never the password itself.',
      'Your profile: what you write about yourself, what you are building, what you are looking for and what you offer, and the facts our matcher uses (your "match profile").',
      'Your profile photo, if you add one. We crop and resize it and remove hidden details such as camera location; members see it next to your name.',
      'Files you upload, such as a resume or portfolio, and the text we extract from them.',
      'What you do on B/N: introductions you accept or decline (and why, if you tell us), messages, posts, comments, reactions, profile views, news you read, searches, and your conversations with Copilot.',
      'Basic technical information needed to run the service, such as when you last signed in.',
    ]],
    ['How we use it', [
      'To run B/N: your account, your profile, messages between members, and email notifications you can switch off.',
      'To find introductions worth making. Our matcher — including an AI model — reads your profile, your match profile and, unless you switch it off, your recent activity (news, searches, Copilot questions, files you work on with Copilot) to suggest people who could help you and whom you could help.',
      'Your activity is used only inside the matcher. It is never shown to other members and never quoted in an introduction.',
      'To answer you in Copilot, and to keep B/N safe — for example, reviewing reports.',
    ]],
    ['Sponsored perks', [
      'Members can promote products to other members as clearly labelled "Sponsored" cards with a perk. We choose who sees a card inside B/N, from your sectors, role, stage and needs. Advertisers never receive your personal information from us.',
      'An advertiser only learns who you are if you choose to: by asking to talk to their team, or by using your perk code with them.',
      'If you claim a perk or talk to an advertiser, we may later ask whether anything came of it. Answering is optional.',
      'Ads never appear inside Copilot answers, introductions or messages.',
    ]],
    ['AI, and where it runs', [
      "Copilot and the matcher run on AI models hosted on our own servers (our Aegis platform). Your profile and chats are not sent to outside AI companies to train their models.",
      'Copilot can make mistakes. Its answers are not legal, financial, tax or investment advice.',
    ]],
    ['Payments', [
      'If you buy storage or credit, you pay in USDC directly to our wallet on a public blockchain (Base or Ethereum). Blockchain transactions are public by nature: anyone can see that your wallet sent the payment.',
      'We record the order, the amount, the network, the transaction ID and the sending wallet address, to match your payment and for our accounts. We never ask for your wallet keys or seed phrase — nobody from B/N ever will.',
    ]],
    ['Who else handles your data', [
      'Google Cloud hosts B/N and its database. Google provides sign-in if you choose "Continue with Google".',
      'Resend sends our emails.',
      'We do not sell your data, and we do not share it with advertisers.',
      'We may disclose information if the law requires it, or to protect members from fraud or harm.',
    ]],
    ['What other members see', [
      'Signed-in members can see your profile. What you are seeking, your files and your Copilot chats are private unless you choose to share them.',
      'When you are introduced to someone, each of you sees a short reason written for you. Neither of you is told whether the other said no.',
      "If you block or report someone, they are not told.",
      'Members can find you in People → Discover and ask to connect, with a note. You choose whether to accept, and you can switch off "Show me in Discover" under My profile.',
      'In a working group, the people in that group see its messages, topics and pins. Pinning a file lets them download it. Leave a group and they keep what was already said.',
    ]],
    ['Your choices', [
      'Edit or remove anything in your profile at any time.',
      'Switch off the use of your activity for introductions under My profile → What the matcher uses.',
      'Choose which emails you get, or turn them all off, under My profile → Email notifications.',
      'Block or report anyone.',
      `Ask us for a copy of your data, or to close your account and delete it, by emailing ${CONTACT}. We keep only what the law requires us to.`,
    ]],
    ['How long we keep it', [
      'While your account is open. When you ask us to delete your account we remove your profile, files and conversations within 30 days, except where we must keep records by law or to resolve a report.',
    ]],
    ['Changes and contact', [
      'If we change this policy in a way that matters, we will ask you to accept the new version when you next use B/N.',
      `Questions or requests: ${CONTACT}. ${OPERATOR}, Philippines.`,
    ]],
  ],
};

export const TERMS = {
  title: 'Terms of Use',
  updated: 'October 9, 2026',
  intro: `These terms are an agreement between you and ${OPERATOR} ("we"), the operator of Builders' Network ("B/N"). By signing in, you agree to them and to our Privacy Policy.`,
  sections: [
    ['A private pilot', [
      'B/N is invite-only and in an early pilot. Features will change, and some may be removed. We provide it as it is, without promises that it will always be available or free of errors.',
      'You must be at least 18 and joining for professional purposes.',
    ]],
    ['Your account', [
      'Use your real name and keep your profile honest. One account per person.',
      'Keep your sign-in details to yourself. You are responsible for what happens under your account.',
    ]],
    ['Respect the network', [
      'No spam, mass messages or unsolicited selling to people who have not asked for it.',
      'No scams, fraud, impersonation, harassment, hate or threats.',
      "Don't share another member's private information or messages without their permission.",
      "Don't scrape, copy or automate access to B/N.",
      'Introductions need both people to agree. Do not try to work around that.',
    ]],
    ['Copilot and AI', [
      'Copilot and the matcher use AI and can be wrong. Check anything important yourself.',
      'Nothing from Copilot is legal, financial, tax or investment advice. Decisions you make are your own.',
      'Copilot can create images. You may use the images you make, but not to deceive or harm anyone: no images of real people, no fake documents, nothing that copies another company\'s brand. Images are checked automatically and some requests are refused. AI images can get details wrong.',
    ]],
    ['Your content', [
      'You own what you post and upload. You let us store, process and display it to run B/N — including showing your profile to other members and using it for matching as our Privacy Policy describes.',
      "Only upload what you have the right to share.",
    ]],
    ['Promoting your product', [
      'Members can promote a product to other members for free during the pilot. Every ad, and every change to it, is reviewed by an admin before it shows.',
      'You are responsible for your ad: it must be honest, about a product you are entitled to promote, and your link must go where it says. Perks you offer must be honoured.',
      'Not allowed: token sales or investment offers, gambling, adult content, anything illegal, misleading or that we would not recommend to a fellow founder.',
      'We may pause or remove any ad at any time, including automatically if members report it or if the link is flagged as unsafe.',
    ]],
    ['Paid storage', [
      'Every member has free file storage (currently 15 MB). You can buy more for 3 or 12 months at a time, paid up front in USDC.',
      'Send exactly the amount shown, in USDC, on the network you chose. Payments sent with another token, on another network or to another address may not be recoverable, and we cannot reverse blockchain transactions.',
      'Extra storage starts when your payment is confirmed on-chain. Buying the same pack again extends it.',
      'When a pack ends nothing is deleted, but you cannot upload more until you are under the free allowance or renew. We remind you by email before it ends.',
      'Refunds: within 7 days of purchase if you have not used the extra storage, to the wallet you paid from. After that, no refunds.',
    ]],
    ['Referrals', [
      'Members can post opportunities and refer people to them. Refer someone only with their permission; a member who is referred confirms before the poster sees their name.',
      'Any referral reward is offered and paid by the company or person who posted it, directly to the referrer. B/N records the referral and its status but is not a party to the reward and does not hold or transfer money.',
      'Posts offering a cash reward are reviewed by an admin before they go live. We may remove any referral post at any time.',
    ]],
    ['AI credit and payments', [
      'Members may receive Copilot credit, for example with an invite. Credit has no cash value and cannot be transferred.',
      'If we introduce paid plans, prices and terms will be shown before you pay.',
    ]],
    ['Suspension', [
      'We may suspend or close accounts that break these terms or put members at risk. You can close your account at any time.',
    ]],
    ['Liability', [
      'To the extent the law allows, we are not liable for indirect losses, or for what members do or agree between themselves. Introductions are suggestions, not endorsements.',
    ]],
    ['Law and contact', [
      'These terms are governed by the laws of the Philippines.',
      `Questions: ${CONTACT}.`,
    ]],
  ],
};
